import {FC, ReactElement, ReactNode} from 'react'

import {Trans} from '@/i18n'
import {IconSize} from '@/theme/icons'
import {theme} from '@/theme/theme'

import {FormTooltip} from '../form/tooltip/FormTooltip'
import {CheckmarkIcon} from '../icons/CheckmarkIcon'
import {InfoIcon} from '../icons/InfoIcon'
import {Text, TextFontWeight, TextVariant} from './Text'

export type ListItemWithCheckmarkProps = {
  text: string | ReactElement<typeof Trans>
  color?: string
  fontWeight?: TextFontWeight
  align?: 'left' | 'center'
  verticalAlign?: 'top' | 'middle'
  icon?: ReactNode
  iconColor?: string
  gap?: string
  tooltipText?: string
  rowGap?: string
  mobileTextVariant?: TextVariant
  // Set to 'div' when the wrapper isn't a <ul>/<ol>/<menu>
  element?: 'li' | 'div'
}

export const ListItemWithCheckmark: FC<ListItemWithCheckmarkProps> = ({
  text,
  color,
  fontWeight,
  align = 'left',
  verticalAlign = 'top',
  icon,
  iconColor = theme.palette.text.primary,
  gap = theme.spacing[2],
  tooltipText,
  rowGap = theme.spacing[2.5],
  mobileTextVariant,
  element: Element = 'li',
}) => (
  <>
    <Element className="item">
      <div className="icon">
        {icon ?? <CheckmarkIcon color={iconColor} size={IconSize.medium} />}
      </div>
      <Text color={color} fontWeight={fontWeight} align={align} mobileVariant={mobileTextVariant}>
        {text}
        {tooltipText && (
          <span className="tooltip">
            <FormTooltip
              tooltipText={tooltipText}
              icon={<InfoIcon color={theme.palette.gray[400]} />}
            />
          </span>
        )}
      </Text>
    </Element>

    <style jsx>{`
      .item {
        display: flex;
        align-items: ${verticalAlign === 'top' ? 'flex-start' : 'center'};
        gap: ${gap};
      }
      .item + .item {
        margin-top: ${rowGap};
      }
      .icon {
        margin-top: ${verticalAlign === 'top' ? theme.spacing[0.5] : 0};
      }
      .tooltip {
        position: relative;
        top: ${verticalAlign === 'top' ? theme.spacing[0.5] : 0};
        margin-left: ${theme.spacing[2]};
      }
    `}</style>
  </>
)
